import api from './api'
import type {
  Document,
  DocumentsResponse,
  DocumentCreate,
  DocumentUpdate,
} from '@/types/document'

export async function getDocuments(params?: {
  status?: string
  vendor?: string
  doc_type?: string
  location?: string
  source_type?: string
  reference_number?: string
  date_from?: string
  date_to?: string
  amount_op?: 'eq' | 'gt' | 'gte' | 'lt' | 'lte'
  amount?: string
  flags?: string
  sort_key?: string
  sort_dir?: 'asc' | 'desc'
  page?: number
  page_size?: number
  /** Comma-separated ids — narrows the list to this cohort. */
  document_ids?: string
}): Promise<DocumentsResponse> {
  const response = await api.get<DocumentsResponse>('/documents', { params })
  return response.data
}

/** Flag filter values that appear in the cohort (same column filters as the table, all pages). */
export async function getDocumentFlagFacets(params?: {
  status?: string
  vendor?: string
  doc_type?: string
  location?: string
  source_type?: string
  reference_number?: string
  date_from?: string
  date_to?: string
  amount_op?: 'eq' | 'gt' | 'gte' | 'lt' | 'lte'
  amount?: string
}): Promise<{ facets: string[] }> {
  const response = await api.get<{ facets: string[] }>('/documents/flag-facets', { params })
  return response.data
}

export interface AmazonReportUploadResponse {
  kind: 'amazon_report'
  summary: {
    created: number
    updated: number
    unchanged: number
    skipped_approved: number
    skipped_deleted: number
    flagged?: number
    errors: string[]
  }
}

export function isAmazonReportUploadResponse(
  value: Document | AmazonReportUploadResponse,
): value is AmazonReportUploadResponse {
  return (
    typeof value === 'object' &&
    value !== null &&
    'kind' in value &&
    value.kind === 'amazon_report'
  )
}

export async function uploadDocument(
  file: File,
  options?: { emailRuleId?: number },
): Promise<Document | AmazonReportUploadResponse> {
  const formData = new FormData()
  formData.append('file', file)
  if (options?.emailRuleId != null) {
    formData.append('email_rule_id', String(options.emailRuleId))
  }
  const response = await api.post<Document | AmazonReportUploadResponse>(
    '/documents/upload',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  )
  return response.data
}

export async function getDocument(id: number): Promise<Document> {
  const response = await api.get<Document>(`/documents/${id}`)
  return response.data
}

export async function rotateDocumentPdfPage(
  documentId: number,
  pageNumber: number,
  degrees: 90 | -90 | 180
): Promise<void> {
  await api.post(`/documents/${documentId}/rotate-pdf-page`, {
    page_number: pageNumber,
    degrees,
  })
}

export async function createDocument(data: DocumentCreate): Promise<Document> {
  const response = await api.post<Document>('/documents', data)
  return response.data
}

export async function updateDocument(id: number, data: DocumentUpdate): Promise<Document> {
  const response = await api.put<Document>(`/documents/${id}`, data)
  return response.data
}

export async function approveDocument(
  id: number,
  options?: { overrideNonPayable?: boolean; overrideQboDuplicate?: boolean }
): Promise<Document> {
  const params = new URLSearchParams()
  if (options?.overrideNonPayable) {
    params.set('override_non_payable', 'true')
  }
  if (options?.overrideQboDuplicate) {
    params.set('override_qbo_duplicate', 'true')
  }
  const url = params.toString()
    ? `/documents/${id}/approve?${params.toString()}`
    : `/documents/${id}/approve`
  const response = await api.post<Document>(url)
  return response.data
}

export async function unapproveDocument(id: number): Promise<Document> {
  const response = await api.post<Document>(`/documents/${id}/unapprove`)
  return response.data
}

export async function overwriteApprovedDuplicate(id: number): Promise<Document> {
  const response = await api.post<Document>(`/documents/${id}/overwrite-approved-duplicate`)
  return response.data
}

export async function deleteDocument(id: number): Promise<Document> {
  const response = await api.post<Document>(`/documents/${id}/delete`)
  return response.data
}

export async function restoreDocument(id: number): Promise<Document> {
  const response = await api.post<Document>(`/documents/${id}/restore`)
  return response.data
}

export async function permanentlyDeleteDocument(id: number): Promise<void> {
  await api.delete(`/documents/${id}`)
}

export async function getDocumentPdfUrl(id: number): Promise<{ url: string; type: string }> {
  const response = await api.get<{ url: string; type: string }>(`/documents/${id}/pdf-url`)
  return response.data
}

export async function refreshDocumentCategories(id: number): Promise<Document> {
  const response = await api.post<Document>(`/documents/${id}/refresh-categories`)
  return response.data
}

export async function allocateSharedCosts(id: number): Promise<Document> {
  const response = await api.post<Document>(`/documents/${id}/allocate-shared-costs`)
  return response.data
}

export async function bulkRefreshCategories(documentIds: number[]): Promise<Document[]> {
  const response = await api.post<Document[]>('/documents/bulk-refresh-categories', documentIds)
  return response.data
}

export interface BulkMatchVendorsResponse {
  success: boolean
  total_reviewed: number
  updated_count: number
  updated_docs: { id: number; vendor_name: string }[]
}

export async function bulkMatchVendors(): Promise<BulkMatchVendorsResponse> {
  const response = await api.post<BulkMatchVendorsResponse>('/documents/bulk-match-vendors')
  return response.data
}

export interface BulkApproveResult {
  success: boolean
  approved_count: number
  failed_count: number
  approved_docs: { id: number; reference_number: string | null }[]
  failed_docs: { id: number; reference_number: string | null; error: string }[]
}

export async function bulkApproveDocuments(documentIds: number[]): Promise<BulkApproveResult> {
  const response = await api.post<BulkApproveResult>('/documents/bulk-approve', documentIds)
  return response.data
}

export interface QboDuplicateInfo {
  id: number
  vendor_name: string
  reference_number: string
  qbo_entity_type: 'Bill' | 'VendorCredit'
  qbo_id: string
  qbo_doc_number: string
}

export interface CheckQboDuplicatesResult {
  success: boolean
  checked_count: number
  duplicate_count: number
  duplicates: QboDuplicateInfo[]
}

export async function checkQboDuplicates(
  documentIds: number[],
): Promise<CheckQboDuplicatesResult> {
  const response = await api.post<CheckQboDuplicatesResult>(
    '/documents/check-qbo-duplicates',
    { document_ids: documentIds },
  )
  return response.data
}

/** Result shape for the Doc Review reprocess-results modal (filled from completed background jobs). */
export interface BulkReprocessResult {
  success: boolean
  reprocessed_count: number
  failed_count: number
  reprocessed_docs: Document[]
  failed_docs: { id: number; reference_number: string | null; error: string }[]
}

/** Only include keys for fields the user chose to change; omitted keys are left unchanged on the server. */
export interface BulkMetadataUpdateRequest {
  document_ids: number[]
  vendor_name?: string | null
  doc_type?: 'bill' | 'vendor_credit' | null
  location?: string | null
  location_id?: string | null
  memo?: string | null
  /** When both are set, every line item on each document gets this category. */
  line_item_category_id?: string | null
  line_item_category_name?: string | null
  /** QBO payment term id in extracted_data; null or empty clears. */
  qbo_sales_term_id?: string | null
}

export async function bulkUpdateDocumentMetadata(
  body: BulkMetadataUpdateRequest
): Promise<Document[]> {
  const response = await api.post<Document[]>('/documents/bulk-update-metadata', body)
  return response.data
}

export interface LineItemCorrectionRequest {
  line_item_indices: number[]
  instructions: string
}

export interface CorrectedLineItem {
  index: number
  description: string
  amount: string
}

export interface LineItemCorrectionResponse {
  corrected_items: CorrectedLineItem[]
}

export async function correctLineItems(
  documentId: number,
  request: LineItemCorrectionRequest
): Promise<LineItemCorrectionResponse> {
  const response = await api.post<LineItemCorrectionResponse>(
    `/documents/${documentId}/correct-line-items`,
    request
  )
  return response.data
}
